import { SECTION_META, SECTION_VARIANTS, sectionContent, type SectionNode, type SectionType } from "@baze/schema";
import type { ReactNode } from "react";
import type { RenderContext, SectionComponent } from "./context";
import { About } from "./sections/about";
import { Contact } from "./sections/contact";
import { Cta } from "./sections/cta";
import { Faq } from "./sections/faq";
import { Footer } from "./sections/footer";
import { Gallery } from "./sections/gallery";
import { Hero } from "./sections/hero";
import { OpeningHours } from "./sections/hours";
import { Location } from "./sections/location";
import { Menu } from "./sections/menu";
import { Navbar } from "./sections/navbar";
import { Pricing } from "./sections/pricing";
import { Products } from "./sections/products";
import { Services } from "./sections/services";
import { Stats } from "./sections/stats";
import { Testimonials } from "./sections/testimonials";
import { Events } from "./sections/events";
import { Countdown } from "./sections/countdown";
import { Donation } from "./sections/donation";
import { Gift } from "./sections/gift";
import { Rsvp } from "./sections/rsvp";
import { BlogLatest } from "./blog";
import { Wishes } from "./sections/wishes";
import { Quote } from "./sections/quote";
import { Team } from "./sections/team";
import { Timeline } from "./sections/timeline";
import { Links } from "./sections/links";
import { Picks } from "./sections/picks";
import { Logos } from "./sections/logos";
import { Videos } from "./sections/videos";
import { Lessons } from "./sections/lessons";
import { ProductFeature } from "./sections/product-feature";

export type ComponentRegistry = { [K in SectionType]: SectionComponent<K> };

/** One component per section type; each switches on `node.variant`. */
export const componentRegistry: ComponentRegistry = {
  navbar: Navbar,
  hero: Hero,
  about: About,
  services: Services,
  menu: Menu,
  products: Products,
  gallery: Gallery,
  testimonials: Testimonials,
  faq: Faq,
  stats: Stats,
  "opening-hours": OpeningHours,
  location: Location,
  contact: Contact,
  pricing: Pricing,
  cta: Cta,
  timeline: Timeline,
  events: Events,
  team: Team,
  quote: Quote,
  countdown: Countdown,
  gift: Gift,
  donation: Donation,
  wishes: Wishes,
  rsvp: Rsvp,
  "blog-latest": BlogLatest,
  links: Links,
  picks: Picks,
  logos: Logos,
  videos: Videos,
  lessons: Lessons,
  "product-feature": ProductFeature,
  footer: Footer,
};

const isSectionType = (t: unknown): t is SectionType => typeof t === "string" && Object.prototype.hasOwnProperty.call(componentRegistry, t);

function EditorWarning({ node, title, detail }: { node: { id?: unknown; type?: unknown }; title: string; detail: string }) {
  const id = typeof node.id === "string" ? node.id : undefined;
  const type = typeof node.type === "string" ? node.type : "unknown";
  return (
    <div data-baze-section={id} data-baze-type={type} data-baze-invalid="" className="mx-auto my-6 w-[calc(100%-2.5rem)] max-w-3xl rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 px-5 py-4 text-amber-950">
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm">{detail}</p>
    </div>
  );
}

/** Allowed variant for the node: its own, else the design default, else the engine default (first). */
export function resolveVariant(type: SectionType, variant: unknown, ctx: Pick<RenderContext, "theme">): string {
  const allowed = SECTION_VARIANTS[type] as readonly string[];
  if (typeof variant === "string" && allowed.includes(variant)) return variant;
  const fromDesign = ctx.theme.design?.variants?.[type];
  if (fromDesign && allowed.includes(fromDesign)) return fromDesign;
  return allowed[0]!;
}

/**
 * Render one section. Never throws: unknown types and invalid content render nothing in live mode
 * and a warning card in the editor. Content is re-validated against the section schema first.
 */
export function renderSection(node: unknown, ctx: RenderContext, registry: ComponentRegistry = componentRegistry): ReactNode {
  if (!node || typeof node !== "object") return null;
  const raw = node as { id?: unknown; type?: unknown; variant?: unknown; content?: unknown };
  if (!isSectionType(raw.type)) {
    return ctx.editor ? <EditorWarning node={raw} title="Section tidak dikenal" detail={`Tipe "${String(raw.type)}" tidak tersedia di versi ini, jadi tidak ditampilkan di website.`} /> : null;
  }
  const type = raw.type;
  const parsed = sectionContent[type].safeParse(raw.content);
  if (!parsed.success) {
    return ctx.editor ? <EditorWarning node={raw} title={`${SECTION_META[type].label}: isi tidak valid`} detail="Periksa isian section ini. Section tidak ditampilkan di website sampai diperbaiki." /> : null;
  }
  const safeNode = { ...(raw as SectionNode), variant: resolveVariant(type, raw.variant, ctx), content: parsed.data } as SectionNode;
  const Component = registry[type] as SectionComponent;
  try {
    // Components are pure functions (no hooks), so calling directly lets us catch render errors here.
    const texts = safeNode.style?.texts;
    const keys = texts && typeof texts === "object" ? Object.keys(texts) : [];
    return Component({ node: safeNode, ctx: keys.length ? { ...ctx, textKeys: new Set(keys) } : ctx });
  } catch (err) {
    if (ctx.editor) {
      return <EditorWarning node={raw} title={`${SECTION_META[type].label}: gagal ditampilkan`} detail={err instanceof Error ? err.message.slice(0, 200) : "Terjadi kesalahan."} />;
    }
    return null;
  }
}
